import { supabase } from "../db";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

const applicationTable = "referral_drafts";
const VALID_STATUSES = ["draft", "hr_reviewed", "finalized"];

export const createEmploymentApplication = async (formData) => {
  const slug_token = uuidv4();
  const { data, error } = await supabase
    .from(applicationTable)
    .insert([{ slug_token, form_data: formData }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const submitEmploymentApplication = async (formData) => {
  const result = await createEmploymentApplication(formData);

  const userData = {
    firstName: formData.firstName || "",
    lastName: formData.lastName || "",
    email: formData.email || "",
    phoneNumber: formData.phoneNumber || formData.employeePhone || "",
  };

  await axios.post("https://api-backend-samah.vercel.app/submit-form", {
    userData,
    formData,
    formType: "Employment Application",
    pdfBuffer: null,
  });

  return result;
};

export const getEmploymentApplicationById = async (id) => {
  const { data, error } = await supabase
    .from(applicationTable)
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
};

export const getEmploymentApplicationBySlug = async (slug_token) => {
  const { data, error } = await supabase
    .from(applicationTable)
    .select("*")
    .eq("slug_token", slug_token)
    .maybeSingle();

  if (error) throw error;
  return data;
};

export const getAllEmploymentApplications = async (filters = {}) => {
  let query = supabase
    .from(applicationTable)
    .select(`
      id,
      slug_token,
      status,
      created_at,
      updated_at,
      form_data
    `)
    .order("created_at", { ascending: false });

  if (filters.status) query = query.eq("status", filters.status);

  if (filters.startDate && filters.endDate) {
    query = query
      .gte("created_at", filters.startDate)
      .lte("created_at", filters.endDate);
  }

  if (filters.search) {
    query = query.or(
      `form_data->>'employeeName' ilike '%${filters.search}%',` +
        `form_data->>'firstName' ilike '%${filters.search}%',` +
        `form_data->>'lastName' ilike '%${filters.search}%',` +
        `form_data->>'email' ilike '%${filters.search}%'`
    );
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
};

export const getPaginatedApplications = async (page = 1, pageSize = 10, filters = {}) => {
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;

  let query = supabase
    .from(applicationTable)
    .select(`
      id,
      slug_token,
      status,
      created_at,
      updated_at,
      form_data
    `, { count: "exact" })
    .order("created_at", { ascending: false })
    .range(start, end);

  if (filters.status) query = query.eq("status", filters.status);

  const { data, error, count } = await query;
  if (error) throw error;

  return {
    applications: data,
    totalCount: count,
    currentPage: page,
    pageSize,
    totalPages: Math.ceil(count / pageSize),
  };
};

export const deleteEmploymentApplication = async (id) => {
  const { error } = await supabase.from(applicationTable).delete().eq("id", id);
  if (error) throw error;
  return { success: true };
};

export const updateFormDataById = async (id, formData) => {
  const { data, error } = await supabase
    .from(applicationTable)
    .update({
      form_data: formData,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateApplicationStatusById = async (id, status) => {
  if (!VALID_STATUSES.includes(status)) {
    throw new Error(
      `Invalid status "${status}". Must be one of: ${VALID_STATUSES.join(", ")}`
    );
  }

  const { data, error } = await supabase
    .from(applicationTable)
    .update({
      status,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select("id, status, updated_at");

  if (error) throw error;

  if (!data || data.length === 0) {
    return { id, status, updated_at: new Date().toISOString() };
  }

  return data[0];
};

export const getApplicationStatus = async (id) => {
  const { data, error } = await supabase
    .from(applicationTable)
    .select("id, status, updated_at")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  return data;
};

export const finalizeApplication = async (id, formData = null) => {
  const { data: existing, error: fetchError } = await supabase
    .from(applicationTable)
    .select("id, status, form_data")
    .eq("id", id)
    .maybeSingle();

  if (fetchError) throw fetchError;
  if (!existing) throw new Error("Application not found");
  if (existing.status === "finalized") {
    throw new Error("Application is already finalized");
  }

  const patch = {
    status: "finalized",
    updated_at: new Date().toISOString(),
  };

  if (formData) {
    patch.form_data = formData;
  }

  const { data, error } = await supabase
    .from(applicationTable)
    .update(patch)
    .eq("id", id)
    .select("id, status, updated_at");

  if (error) throw error;
  return data?.[0] ?? { id, status: "finalized", updated_at: patch.updated_at };
};